import {
  createContext,
  type FormEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  APIError,
  api,
  type Actor,
  type AuthSession,
  type Membership,
  type Role,
  setCSRFToken,
} from "../api/client";
import {
  createPasskey,
  getPasskey,
  type JSONCreationOptions,
  type JSONRequestOptions,
  passkeyErrorMessage,
} from "./webauthn";

type AuthStatus = "checking" | "authenticated" | "unauthenticated";
type AuthView = "signin" | "signup" | "recover";

interface AuthContextValue {
  actor: Actor | null;
  memberships: Membership[];
  status: AuthStatus;
  error: string | null;
  recoveryCodes: string[] | null;
  register: (
    handle: string,
    displayName: string,
    invitationToken?: string,
  ) => Promise<void>;
  refreshIdentity: () => Promise<void>;
  signIn: () => Promise<void>;
  recover: (handle: string, recoveryCode: string) => Promise<void>;
  signOut: () => Promise<void>;
  signOutAll: () => Promise<void>;
  clearRecoveryCodes: () => void;
  roleFor: (organizationId: string) => Role | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [actor, setActor] = useState<Actor | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  const clearIdentity = useCallback((): void => {
    setCSRFToken(null);
    setActor(null);
    setMemberships([]);
    setRecoveryCodes(null);
    setStatus("unauthenticated");
  }, []);

  const loadIdentity = useCallback(async (quiet: boolean): Promise<void> => {
    try {
      const identity = await api.me();
      setActor(identity.actor);
      setMemberships(
        identity.memberships.filter(
          (membership) => membership.status === "active",
        ),
      );
      setError(null);
      setStatus("authenticated");
    } catch (cause) {
      setActor(null);
      setMemberships([]);
      setStatus("unauthenticated");
      if (
        cause instanceof APIError &&
        cause.code !== "authentication_required" &&
        cause.code !== "authentication_failed"
      ) {
        setError(`${cause.message} (request ${cause.requestId})`);
      }
      if (!quiet) throw cause;
    }
  }, []);

  useEffect(() => {
    void loadIdentity(true);
  }, [loadIdentity]);

  const completeSession = useCallback(
    async (session: AuthSession): Promise<void> => {
      setCSRFToken(session.csrf_token);
      setStatus("checking");
      setError(null);
      try {
        await loadIdentity(false);
      } catch (cause) {
        clearIdentity();
        throw cause;
      }
    },
    [clearIdentity, loadIdentity],
  );

  const register = useCallback(
    async (
      handle: string,
      displayName: string,
      invitationToken?: string,
    ): Promise<void> => {
      setError(null);
      try {
        const options = await api.registerOptions(
          handle,
          displayName,
          invitationToken,
        );
        const credential = await createPasskey(
          options.publicKey as JSONCreationOptions,
        );
        const result = await api.registerVerify(
          options.challenge_id,
          credential,
        );
        setRecoveryCodes(result.recovery_codes);
        await completeSession(result);
      } catch (cause) {
        setStatus("unauthenticated");
        throw cause;
      }
    },
    [completeSession],
  );

  const signIn = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const options = await api.loginOptions();
      const credential = await getPasskey(
        options.publicKey as JSONRequestOptions,
      );
      await completeSession(
        await api.loginVerify(options.challenge_id, credential),
      );
    } catch (cause) {
      setStatus("unauthenticated");
      throw cause;
    }
  }, [completeSession]);

  const recover = useCallback(
    async (handle: string, recoveryCode: string): Promise<void> => {
      setError(null);
      try {
        const options = await api.recoveryOptions(handle, recoveryCode);
        const credential = await createPasskey(
          options.publicKey as JSONCreationOptions,
        );
        await completeSession(
          await api.recoveryVerify(options.challenge_id, credential),
        );
      } catch (cause) {
        setStatus("unauthenticated");
        throw cause;
      }
    },
    [completeSession],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await api.logout();
    } finally {
      clearIdentity();
      setError(null);
    }
  }, [clearIdentity]);

  const signOutAll = useCallback(async (): Promise<void> => {
    try {
      await api.logoutAll();
    } finally {
      clearIdentity();
      setError(null);
    }
  }, [clearIdentity]);

  const value = useMemo<AuthContextValue>(
    () => ({
      actor,
      memberships,
      status,
      error,
      recoveryCodes,
      register,
      refreshIdentity: () => loadIdentity(false),
      signIn,
      recover,
      signOut,
      signOutAll,
      clearRecoveryCodes: () => setRecoveryCodes(null),
      roleFor: (organizationId: string): Role | null =>
        memberships.find(
          (membership) =>
            membership.organization_id === organizationId &&
            membership.status === "active",
        )?.role ?? null,
    }),
    [
      actor,
      error,
      memberships,
      loadIdentity,
      recover,
      recoveryCodes,
      register,
      signIn,
      signOut,
      signOutAll,
      status,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

export function SignIn({
  initialView = "signin",
  invitationToken,
  invitationOrganizationName,
}: {
  initialView?: AuthView;
  invitationToken?: string;
  invitationOrganizationName?: string;
}): ReactElement {
  const { register, signIn, recover, error, status } = useAuth();
  const [view, setView] = useState<AuthView>(initialView);
  const [handle, setHandle] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const busy = status === "checking" || working;

  async function submitRegistration(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setWorking(true);
    if (!/^[a-z0-9][a-z0-9_-]{2,31}$/.test(handle) || !displayName.trim()) {
      setFormError(
        "Use a 3–32 character lowercase handle and enter your display name.",
      );
      setWorking(false);
      return;
    }
    try {
      await register(handle, displayName, invitationToken);
    } catch (cause) {
      setFormError(passkeyErrorMessage(cause));
    } finally {
      setWorking(false);
    }
  }

  async function submitRecovery(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setWorking(true);
    if (!handle.trim() || !recoveryCode.trim()) {
      setFormError("Enter your handle and one unused recovery code.");
      setWorking(false);
      return;
    }
    try {
      await recover(handle, recoveryCode);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "NotAllowedError") {
        setFormError(passkeyErrorMessage(cause));
      } else {
        setFormError(
          "Account recovery failed. Check the details or try another unused code.",
        );
      }
    } finally {
      setWorking(false);
    }
  }

  async function usePasskey(): Promise<void> {
    setFormError(null);
    setWorking(true);
    try {
      await signIn();
    } catch (cause) {
      setFormError(passkeyErrorMessage(cause));
    } finally {
      setWorking(false);
    }
  }

  return (
    <main className="signin-shell">
      <section className="signin-card" aria-labelledby="signin-title">
        <a className="auth-wordmark" href="/" aria-label="VibeCAD home">
          <AuthVibeCADLogo />
          <span>VibeCAD</span>
        </a>
        <p className="eyebrow">
          {invitationOrganizationName
            ? `Invitation to ${invitationOrganizationName}`
            : "VibeCAD workspace"}
        </p>
        <h1 id="signin-title">
          {invitationOrganizationName
            ? `Join ${invitationOrganizationName} securely.`
            : "Secure CAD collaboration, without passwords."}
        </h1>
        <p className="lede">
          {invitationOrganizationName
            ? "Sign in with an existing passkey or create an account. This invitation stays bound to this team and is never pasted into another site."
            : "Create an owner workspace with Face ID, Touch ID, Windows Hello, your device screen lock, or a hardware security key."}
        </p>
        <div className="auth-tabs" aria-label="Account access">
          <button
            className={view === "signin" ? "active" : ""}
            onClick={() => setView("signin")}
            type="button"
          >
            Sign in
          </button>
          <button
            className={view === "signup" ? "active" : ""}
            onClick={() => setView("signup")}
            type="button"
          >
            Create account
          </button>
          <button
            className={view === "recover" ? "active" : ""}
            onClick={() => setView("recover")}
            type="button"
          >
            Recover
          </button>
        </div>
        {view === "signin" && (
          <div className="token-entry token-entry-primary">
            <p>Use a passkey already saved to this device or security key.</p>
            <button
              className="button button-primary button-wide"
              disabled={busy}
              onClick={() => void usePasskey()}
              type="button"
            >
              {busy ? "Checking passkey…" : "Sign in with passkey"}
            </button>
          </div>
        )}
        {view === "signup" && (
          <form
            className="token-entry token-entry-primary"
            onSubmit={(event) => void submitRegistration(event)}
          >
            <label htmlFor="signup-handle">Unique handle</label>
            <input
              id="signup-handle"
              autoComplete="username"
              maxLength={32}
              pattern={"[a-z0-9][a-z0-9_\\-]{2,31}"}
              required
              value={handle}
              onChange={(event) => setHandle(event.target.value.toLowerCase())}
            />
            <label htmlFor="signup-name">Display name</label>
            <input
              id="signup-name"
              autoComplete="name"
              maxLength={80}
              required
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
            <button
              className="button button-primary button-wide"
              disabled={busy}
              type="submit"
            >
              {busy ? "Creating passkey…" : "Create account with passkey"}
            </button>
          </form>
        )}
        {view === "recover" && (
          <form
            className="token-entry token-entry-primary"
            onSubmit={(event) => void submitRecovery(event)}
          >
            <label htmlFor="recovery-handle">Handle</label>
            <input
              id="recovery-handle"
              autoComplete="username"
              value={handle}
              onChange={(event) => setHandle(event.target.value.toLowerCase())}
            />
            <label htmlFor="recovery-code">Unused recovery code</label>
            <input
              id="recovery-code"
              autoComplete="off"
              value={recoveryCode}
              onChange={(event) => setRecoveryCode(event.target.value)}
            />
            <button
              className="button button-primary button-wide"
              disabled={busy}
              type="submit"
            >
              {busy ? "Verifying…" : "Verify and create replacement passkey"}
            </button>
          </form>
        )}
        {(formError || error) && (
          <div className="alert alert-danger" role="alert">
            {formError || error}
          </div>
        )}
        <p className="fine-print">
          No password, biometric, private key, or raw session token is stored by
          VibeCAD. CAD files remain opaque and are never executed.
        </p>
      </section>
      <aside className="signin-visual" aria-label="Security principles">
        <div className="orbit orbit-one" />
        <div className="orbit orbit-two" />
        <div className="principle-card principle-one">
          <span>01</span>
          <strong>Phishing-resistant passkeys</strong>
          <small>Bound to vibecad.studio</small>
        </div>
        <div className="principle-card principle-two">
          <span>02</span>
          <strong>One active editor</strong>
          <small>Exclusive, expiring checkout leases</small>
        </div>
        <div className="principle-card principle-three">
          <span>03</span>
          <strong>Immutable history</strong>
          <small>Verified opaque content</small>
        </div>
      </aside>
    </main>
  );
}

function AuthVibeCADLogo(): ReactElement {
  return (
    <svg
      className="vibecad-logo"
      xmlns="http://www.w3.org/2000/svg"
      width="52"
      height="52"
      viewBox="0 0 64 64"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id="auth-vibecad-mark"
          x1="10"
          y1="54"
          x2="54"
          y2="10"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#1c7ed6" />
          <stop offset="1" stopColor="#74c0fc" />
        </linearGradient>
      </defs>
      <rect x="6" y="6" width="52" height="52" rx="13" fill="#0e1116" />
      <path
        d="M18 18h12l-4.8 16.6c-.4 1.5.7 2.9 2.2 2.9h2.5l-3.1 8.5h-8.7l2.2-6.3c-5.6-2.2-9.1-7.4-9.1-13.8 0-3.1.8-5.8 2.4-7.9z"
        fill="url(#auth-vibecad-mark)"
      />
      <path
        d="M34 18h12.2c4.6 0 7.8 3.1 7.8 7.6 0 3.3-1.8 5.8-4.8 7 2.2.9 3.6 3.1 3.6 6 0 5-3.5 8.4-8.7 8.4H31l2.7-8.5h8.2c1.6 0 2.7-1 2.7-2.4s-1-2.3-2.7-2.3h-5.8l2.3-7.3h5.4c1.5 0 2.5-.9 2.5-2.3 0-1.3-1-2.1-2.5-2.1H32.7L34 18z"
        fill="#e8edf2"
      />
      <path
        d="M20.2 50h23.6"
        fill="none"
        stroke="#4dabf7"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
    </svg>
  );
}
